// Lib
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { convertToRaw } from 'draft-js';
import classNames from 'classnames';

// Utils
import {
    getCellRenderContentState,
    getCellTextAlignment,
    getBackgroundColorClass,
} from '../utils/tableCellFormattingUtils';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';

// Components
import CellCheckboxRenderer from '../cellTypes/CellCheckboxRenderer';
import CellFormulaErrorRenderer from './CellFormulaErrorRenderer';
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';

// Constants
import { CellTypeConstants, CellTypeNames } from '../cellTypes/CellTypeConstants';

import './MilanoteCellRendererComponent.scss';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';

const MilanoteCellRendererComponent = (props) => {
    const { row, col, hotCellValue, cellData = {}, hotTableInstance, hide } = props;
    const { type, textAlignment, textStyle, background } = cellData;

    const [hfType, hfDetailedType, hfCellValue] = useMemo(() => {
        const sheet = hyperFormulaInstance.getSheetId(hotTableInstance.milanoteProps.elementId);
        if (sheet === undefined) return [null, null, null];

        const hfType = hyperFormulaInstance.getCellType({ sheet, row, col });
        const hfDetailedType = hyperFormulaInstance.getCellValueDetailedType({ sheet, row, col });
        const hfCellValue = hyperFormulaInstance.getCellValue({ sheet, row, col });

        return [hfType, hfDetailedType, hfCellValue];
    }, [hotCellValue, cellData]);

    const textContent = useMemo(
        () =>
            convertToRaw(
                getCellRenderContentState(cellData, hotCellValue, {
                    hfType,
                    hfDetailedType,
                }),
            ),
        [hotCellValue, cellData],
    );

    const editorTextAlignment = useMemo(() => getCellTextAlignment(cellData, hotCellValue), [hotCellValue, cellData]);

    const backgroundClasses = useMemo(() => getBackgroundColorClass(cellData), [background]);

    if (hide) return null;

    const className = classNames('MilanoteCellRendererComponent', backgroundClasses);

    const isFormula = hfType === 'FORMULA' || hfType === 'ARRAYFORMULA';
    const isFormulaError =
        isFormula && hfDetailedType === 'ERROR' && typeof hfCellValue === 'object' && 'message' in hfCellValue;

    if (isFormulaError) {
        return (
            <div className={className}>
                <CellFormulaErrorRenderer cellError={hfCellValue} />
            </div>
        );
    }

    if (type?.name === CellTypeNames.CHECKBOX) {
        const alignment = textAlignment || CellTypeConstants[type.name].alignment;
        const isEditable = !isFormula && !hotTableInstance.milanoteProps.isReadOnly;

        return (
            <div
                className={classNames(className, 'button-cell-renderer', `align-${alignment}`, {
                    editable: isEditable,
                })}
            >
                <CellCheckboxRenderer
                    row={row}
                    col={col}
                    textContent={hotCellValue}
                    isEditable={isEditable}
                    hotTableInstance={hotTableInstance}
                />
            </div>
        );
    }

    return (
        <div className={className}>
            <TableCellEditor
                className="handsontable-renderer"
                isEditing={false}
                isEditable={false}
                textContent={textContent}
                defaultStyleOverride={textStyle}
                textAlignment={editorTextAlignment}
                editorId={`${hotTableInstance.milanoteProps.elementId}-${TABLE_CELL_EDITOR_KEY}-RENDERER`}
                editorKey={`${TABLE_CELL_EDITOR_KEY}-RENDERER`}
            />
        </div>
    );
};

MilanoteCellRendererComponent.propTypes = {
    row: PropTypes.number,
    col: PropTypes.number,
    hotCellValue: PropTypes.any,
    cellData: PropTypes.object,
    hotTableInstance: PropTypes.object,
    hide: PropTypes.bool,
};

export default MilanoteCellRendererComponent;
